import { useMemo, useState } from "react";
import { setDone } from "../../tasks/earlyDone";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDocSelector } from "../../core/useDocSelector";
import type { FilterAst } from "../../core/filter";
import { addToGroup, listPropDefs, removeFromGroup, setProp } from "../../core/ops";
import { groupsOf } from "../../core/structure";
import type { Task } from "../../core/schema";
import { FILTER_SCHEMA, visibleTasks } from "../shared";

export interface KanbanConfig {
  filter?: FilterAst;
  /** "prop:<enumId>" | "done" | "group:<id,id,…>" */
  columns?: string;
}

interface Column {
  id: string;
  label: string;
  tasks: Task[];
}

/** Kanban (spec §9.7): dragging a card between columns writes the underlying prop / done / group. */
export function KanbanView({ config, ctx }: ViewProps<KanbanConfig>) {
  const { t, i18n } = useTranslation();
  const by = config.columns ?? "prop:priority";
  const data = useDocSelector(
    ctx.handle,
    (doc) => {
      const v = visibleTasks(doc, config.filter, Date.now(), { includeDone: true });
      const roots = v.tasks.filter((x) => v.snapshot.parentOf(x.id) === null);
      return {
        tasks: roots,
        groupsOfTask: Object.fromEntries(roots.map((x) => [x.id, groupsOf(v.snapshot, x.id)])),
        propDefs: listPropDefs(doc),
        groups: doc.getMap("groups").toJSON() as Record<string, { title: string }>,
      };
    },
    [config],
  );
  const [over, setOver] = useState<string | null>(null);

  const columns: Column[] = useMemo(() => {
    if (!data) return [];
    const { tasks } = data;
    if (by === "done") {
      return [
        { id: "open", label: t("kanban.open"), tasks: tasks.filter((x) => !x.doneAt) },
        { id: "done", label: t("kanban.done"), tasks: tasks.filter((x) => x.doneAt) },
      ];
    }
    if (by.startsWith("group:")) {
      const ids = by.slice(6).split(",").filter(Boolean);
      const list = ids.length ? ids : Object.keys(data.groups);
      const open = tasks.filter((x) => !x.doneAt);
      return [
        {
          id: "",
          label: t("kanban.none"),
          tasks: open.filter((x) => !list.some((g) => data.groupsOfTask[x.id]?.includes(g))),
        },
        ...list.map((g) => ({
          id: g,
          label: data.groups[g]?.title ?? g,
          tasks: open.filter((x) => data.groupsOfTask[x.id]?.includes(g)),
        })),
      ];
    }
    const propId = by.startsWith("prop:") ? by.slice(5) : "priority";
    const def = data.propDefs.find((d) => d.id === propId);
    const values = ((def?.spec.values as { id: string; label: string }[] | undefined) ?? []).map((v) => ({
      id: v.id,
      label: t(`enum.${propId}.${v.id}`, { defaultValue: v.label }),
    }));
    const dflt = def?.spec.default as string | undefined;
    const open = tasks.filter((x) => !x.doneAt);
    const valueOf = (x: Task) => {
      const v = x.props[propId];
      return Array.isArray(v) ? v[0] : ((v as string | undefined) ?? dflt ?? "");
    };
    const cols = values.map((v) => ({
      id: v.id,
      label: v.label,
      tasks: open.filter((x) => valueOf(x) === v.id),
    }));
    const rest = open.filter((x) => !values.some((v) => v.id === valueOf(x)));
    return rest.length || !dflt ? [{ id: "", label: t("kanban.none"), tasks: rest }, ...cols] : cols;
  }, [data, by, t]);

  const drop = (taskId: string, col: string) => {
    const task = data?.tasks.find((x) => x.id === taskId);
    if (!task) return;
    if (by === "done") {
      if ((col === "done") !== Boolean(task.doneAt))
        setDone(ctx.write, t, i18n.language, task, col === "done");
      return;
    }
    ctx.write((doc) => {
      if (by.startsWith("group:")) {
        for (const g of data?.groupsOfTask[taskId] ?? [])
          if (g !== col && columns.some((c) => c.id === g)) removeFromGroup(doc, g, taskId);
        if (col) addToGroup(doc, col, taskId);
      } else {
        const propId = by.startsWith("prop:") ? by.slice(5) : "priority";
        const def = data?.propDefs.find((d) => d.id === propId);
        setProp(doc, taskId, propId, col ? (def?.spec.multi ? [col] : col) : null);
      }
    });
  };

  return (
    // as many columns side by side as fit at 10 rem; the rest wrap below (never a sideways scrollbar)
    <div
      // at least the widget's height (tall drop targets), taller when the cards need it: the widget scrolls then
      className="grid min-h-full grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-3"
      data-testid="kanban-view"
    >
      {columns.map((c) => (
        <section
          key={c.id}
          data-testid="kanban-column"
          data-column={c.id}
          className={clsx(
            "flex min-h-24 min-w-0 flex-col rounded-md border bg-[var(--surface-2)] p-2",
            over === c.id
              ? "border-accent shadow-[0_0_0_3px_var(--color-accent-tint)]"
              : "border-neutral-200 dark:border-neutral-800",
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(c.id);
          }}
          onDragLeave={() => setOver((o) => (o === c.id ? null : o))}
          onDrop={(e) => {
            e.preventDefault();
            setOver(null);
            const id = e.dataTransfer.getData("text/drang-task");
            if (id) drop(id, c.id);
          }}
        >
          <h3 className="mb-2 flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
            {c.label} <span className="font-mono font-normal">{c.tasks.length}</span>
          </h3>
          <ul className="flex flex-col gap-1.5">
            {c.tasks.map((task) => (
              <li
                key={task.id}
                draggable={!ctx.readOnly}
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/drang-task", task.id);
                  e.dataTransfer.effectAllowed = "move";
                }}
                data-testid="kanban-card"
                data-task-id={task.id}
                className="relative cursor-grab rounded-md border border-neutral-200 bg-[var(--surface)] px-3 py-2 text-sm shadow-[var(--shadow-sm)] dark:border-neutral-800"
              >
                <button
                  type="button"
                  className={clsx(
                    "w-full truncate text-left hover:text-accent",
                    task.doneAt && "text-neutral-500 line-through",
                  )}
                  onClick={() => ctx.openTask(task.id)}
                >
                  {task.title || t("task.untitled")}
                </button>
                {/* keyboard route for the drag: a select that moves the card; shown on touch screens */}
                <label className="sr-only touch:not-sr-only touch:mt-1 touch:flex touch:items-center touch:gap-2 touch:text-[12px] touch:text-neutral-500">
                  {t("kanban.moveTo")}
                  <select
                    value={c.id}
                    onChange={(e) => drop(task.id, e.target.value)}
                    className="touch:h-11 touch:border touch:border-neutral-200 touch:bg-[var(--surface)] dark:touch:border-neutral-800 touch:min-w-0 touch:flex-1 touch:rounded-sm touch:px-2 touch:text-[13px]"
                  >
                    {columns.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

registerViewType<KanbanConfig>({
  id: "kanban",
  label: "views.kanban.label",
  icon: "columns",
  level: "standard",
  defaultConfig: { columns: "prop:priority" },
  configSchema: {
    type: "object",
    properties: {
      columns: { type: "string", title: "config.kanbanColumns", "x-drang": "columns" },
      filter: FILTER_SCHEMA,
    },
  },
  Component: KanbanView,
  minSize: { w: 4, h: 4 },
});
